/** The tax desk at a glance: workload by stage and return type, deadlines, reviews, extensions and rejections. */
import { RETURN_TYPE_LABELS, type ReturnType } from "@clientflow/shared";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "wouter";
import { Empty, Loading, PageHeader, Pager, Section, StatRow, usePaged } from "@/components/page";
import { daysBetween, formatDate } from "@/lib/format";
import { useMemberNames } from "@/lib/members";
import { cn } from "@/lib/utils";

type Row = {
  id: string;
  clientId: string;
  clientName: string;
  name: string;
  returnType: ReturnType | null;
  stageKey: string;
  stageName: string;
  assignedUserId: string | null;
  reviewerUserId: string | null;
  signedOff: boolean;
  due: string | null;
  original: string | null;
  extended: boolean;
};

type Overview = {
  today: string;
  totals: { open: number; inProgress: number };
  byStage: Array<{ key: string; name: string; count: number }>;
  byType: Array<{ type: string; count: number }>;
  pastDue: Row[];
  dueThisWeek: Row[];
  awaitingReview: Row[];
  extensionsToFile: Row[];
  rejections: Array<{ id: string; clientId: string; clientName: string; form: string; jurisdiction: string; taxYear: number; rejectedOn: string; code: string; reason: string; requestStatus: string | null }>;
  refundInquiries: Array<{ id: string; clientId: string; clientName: string; form: string; jurisdiction: string; refundCents: number | null; reportedAt: string }>;
  deadlines: Array<{ date: string; clientId: string; clientName: string; engagementId: string; what: string }>;
  notices: Array<{ id: string; clientId: string; clientName: string; authority: string; noticeCode: string; status: "new" | "open"; responseDueOn: string | null; assignedUserId: string | null }>;
};

const typeLabel = (t: string) => (t === "other" ? "Other tax work" : (RETURN_TYPE_LABELS[t as ReturnType] ?? t));
const shortType = (t: ReturnType | null) => (t ? `Form ${t === "1120S" ? "1120-S" : t}` : null);

/** Ten at a time of a list on this page, with a compact Previous / Next below when there are more. */
function PagedList<T>({ items, noun, empty, children }: { items: T[]; noun: string; empty: string; children: (page: T[]) => ReactNode }) {
  const paged = usePaged(items);
  if (!items.length) return <p className="text-sm text-muted-foreground">{empty}</p>;
  return (
    <>
      {children(paged.items)}
      <Pager paged={paged} noun={noun} compact className="mt-3" />
    </>
  );
}

/** A list of returns, each linking to its engagement. */
function ReturnList({ rows, empty, right }: { rows: Row[]; empty: string; right: (r: Row) => ReactNode }) {
  return (
    <PagedList items={rows} noun="returns" empty={empty}>
      {(page) => (
    <ul className="-my-2 divide-y divide-border">
      {page.map((r) => (
        <li key={r.id}>
          <Link href={`/engagements/${r.id}`} className="flex items-center gap-3 py-2.5 hover:bg-muted/30">
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{r.clientName}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {shortType(r.returnType) ?? r.name} · {r.stageName}
              </span>
            </span>
            <span className="shrink-0 text-right text-xs">{right(r)}</span>
          </Link>
        </li>
      ))}
    </ul>
      )}
    </PagedList>
  );
}

export function TaxOverviewPage() {
  const { data, isLoading } = useQuery<Overview>({ queryKey: ["/api/tax/overview"] });
  const names = useMemberNames();
  if (isLoading) return <Loading />;
  if (!data) return <Empty>The tax overview could not be loaded.</Empty>;
  const maxStage = Math.max(1, ...data.byStage.map((s) => s.count));
  const totalTypes = Math.max(1, data.byType.reduce((a, b) => a + b.count, 0));

  return (
    <>
      <PageHeader
        crumbs={[{ label: "Tax", href: "/tax" }]}
        title="Tax Overview"
        description="Where every return stands, what is due, and what needs attention."
        actions={
          <Link href="/tax/returns" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
            All tax returns <ArrowRight className="h-4 w-4" />
          </Link>
        }
      />
      <StatRow
        stats={[
          { label: "Past due", value: data.pastDue.length, tone: data.pastDue.length ? "danger" : "success" },
          { label: "Due in the next 7 days", value: data.dueThisWeek.length, tone: data.dueThisWeek.length ? "warning" : "default" },
          { label: "Awaiting review sign-off", value: data.awaitingReview.length, tone: data.awaitingReview.length ? "warning" : "default" },
          { label: "Open e-file rejections", value: data.rejections.length, tone: data.rejections.length ? "danger" : "success" },
        ]}
      />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Section title="Returns by stage" description={`${data.totals.open} open · ${data.totals.inProgress} not yet filed`}>
          {!data.byStage.length ? (
            <p className="text-sm text-muted-foreground">No open tax work.</p>
          ) : (
            <ul className="space-y-2.5">
              {data.byStage.map((s) => (
                <li key={s.key} className="grid grid-cols-[minmax(0,10.5rem)_1fr_2rem] items-center gap-3 text-sm">
                  <span className="truncate">{s.name}</span>
                  <span className="h-2.5 overflow-hidden rounded-full bg-muted">
                    <span className="block h-full rounded-full bg-primary" style={{ width: `${(s.count / maxStage) * 100}%` }} />
                  </span>
                  <span className="text-right font-medium tabular">{s.count}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
        <Section title="Returns by type">
          {!data.byType.length ? (
            <p className="text-sm text-muted-foreground">No open tax work.</p>
          ) : (
            <ul className="space-y-2.5">
              {[...data.byType]
                .sort((a, b) => b.count - a.count)
                .map((t) => (
                  <li key={t.type} className="flex items-center justify-between gap-3 text-sm">
                    <span className="min-w-0 truncate">{typeLabel(t.type)}</span>
                    <span className="shrink-0 tabular">
                      <span className="font-medium">{t.count}</span> <span className="text-xs text-muted-foreground">{Math.round((t.count / totalTypes) * 100)}%</span>
                    </span>
                  </li>
                ))}
            </ul>
          )}
        </Section>

        <Section title="Past due and due this week" description="Due dates follow filed extensions.">
          <ReturnList
            rows={[...data.pastDue, ...data.dueThisWeek]}
            empty="Nothing is past due or due in the next 7 days."
            right={(r) => {
              const days = daysBetween(data.today, r.due!);
              return (
                <>
                  <span className={cn("block font-medium tabular", days < 0 ? "text-red-700 dark:text-red-300" : "text-amber-700 dark:text-amber-300")}>
                    {days < 0 ? `${-days} day${days === -1 ? "" : "s"} late` : days === 0 ? "Due today" : `Due in ${days} day${days === 1 ? "" : "s"}`}
                  </span>
                  <span className="text-muted-foreground tabular">
                    {formatDate(r.due)}
                    {r.extended && " (extended)"}
                  </span>
                </>
              );
            }}
          />
        </Section>
        <Section title="Awaiting review sign-off" description="Returns cannot go to the client for signature until reviewed.">
          <ReturnList
            rows={data.awaitingReview}
            empty="No returns are waiting for review."
            right={(r) => (
              <>
                <span className="block">Reviewer: {r.reviewerUserId ? (names.get(r.reviewerUserId) ?? "—") : <span className="text-amber-700 dark:text-amber-300">not assigned</span>}</span>
                <span className="text-muted-foreground">Preparer: {r.assignedUserId ? (names.get(r.assignedUserId) ?? "—") : "—"}</span>
              </>
            )}
          />
        </Section>

        <Section title="Open e-file rejections" description="Correct and file again; the client is asked for anything we need.">
          <PagedList items={data.rejections} noun="rejections" empty="No open rejections.">
            {(page) => (
            <ul className="-my-2 divide-y divide-border">
              {page.map((x) => (
                <li key={x.id}>
                  <Link href={`/clients/${x.clientId}?tab=tax`} className="flex items-center gap-3 py-2.5 hover:bg-muted/30">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{x.clientName}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {x.taxYear} {x.jurisdiction === "federal" ? "Federal" : x.jurisdiction} Form {x.form}
                        {x.code && ` · ${x.code}`} · {x.reason}
                      </span>
                    </span>
                    <span className="shrink-0 text-right text-xs">
                      <span className="block tabular">Rejected {formatDate(x.rejectedOn)}</span>
                      <span className="text-muted-foreground">{x.requestStatus ? (x.requestStatus === "open" ? "Waiting on client" : "Client responded") : "Firm to correct"}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            )}
          </PagedList>
        </Section>
        <Section title="Extensions to file" description="Original due date within 30 days and no extension recorded.">
          <ReturnList
            rows={data.extensionsToFile}
            empty="No returns need an extension in the next 30 days."
            right={(r) => <span className={cn("tabular", r.original! < data.today && "text-red-700 dark:text-red-300")}>Due {formatDate(r.original)}</span>}
          />
        </Section>

        <Section title="Deadlines in the next 30 days">
          <PagedList items={data.deadlines} noun="deadlines" empty="No deadlines in the next 30 days.">
            {(page) => (
            <ol className="relative space-y-3 border-l border-border pl-4">
              {page.map((d) => (
                <li key={`${d.engagementId}-${d.date}`} className="relative">
                  <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-card bg-primary" aria-hidden />
                  <Link href={`/engagements/${d.engagementId}`} className="block hover:underline">
                    <span className="text-xs font-medium text-muted-foreground tabular">{formatDate(d.date)}</span>
                    <span className="block text-sm">
                      <span className="font-medium">{d.clientName}</span> · {d.what}
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
            )}
          </PagedList>
        </Section>
        <Section title="IRS & state notices" description="Open notices by response date." action={<Link href="/tax/notices" className="text-xs font-medium text-primary hover:underline">All notices</Link>}>
          <PagedList items={data.notices} noun="notices" empty="No open notices.">
            {(page) => (
            <ul className="-my-2 divide-y divide-border">
              {page.map((n) => (
                <li key={n.id}>
                  <Link href={`/tax/notices?notice=${n.id}`} className="flex items-center gap-3 py-2.5 hover:bg-muted/30">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{n.clientName}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {n.authority === "federal" ? "IRS" : n.authority} {n.noticeCode || "notice"} · {n.assignedUserId ? (names.get(n.assignedUserId) ?? "—") : "Unassigned"}
                      </span>
                    </span>
                    <span className={cn("shrink-0 text-xs tabular", n.responseDueOn && n.responseDueOn < data.today && "text-red-700 dark:text-red-300")}>
                      {n.status === "new" ? "Sent by client" : n.responseDueOn ? `Respond by ${formatDate(n.responseDueOn)}` : "No response date"}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            )}
          </PagedList>
        </Section>
        <Section title="Refund inquiries" description="Clients who reported a refund not received." className="lg:col-span-2">
          <PagedList items={data.refundInquiries} noun="inquiries" empty="No open refund inquiries.">
            {(page) => (
            <ul className="-my-2 divide-y divide-border">
              {page.map((x) => (
                <li key={x.id}>
                  <Link href="/tax/refunds" className="flex items-center gap-3 py-2.5 hover:bg-muted/30">
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{x.clientName}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {x.jurisdiction === "federal" ? "Federal" : x.jurisdiction} · reported {formatDate(x.reportedAt.slice(0, 10))}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            )}
          </PagedList>
        </Section>
      </div>
    </>
  );
}
